import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'

const {
  Button,
  IconButton,
  FieldRoot,
  FieldLabel,
  FieldDescription,
  FieldError
} = Base

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const MAX_BYTES = 10 * 1024 * 1024
const MAX_PHOTOS = 5

interface IncidentFilePickerProps {
  label: string
  description: string
  buttonLabel: string
  multiple?: boolean
  files: File[]
  onChange: (files: File[]) => void
}

const IncidentFilePicker: React.FC<IncidentFilePickerProps> = ({
  label,
  description,
  buttonLabel,
  multiple = false,
  files,
  onChange
}) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string>()

  const pick = (picked: File[]) => {
    const rejected = picked.find(
      (file) =>
        !ACCEPTED_TYPES.includes(file.type) ||
        file.size === 0 ||
        file.size > MAX_BYTES
    )
    if (rejected) {
      setError(
        t('process_details_page.report_incident.file_rejected', {
          name: rejected.name
        })
      )
      return
    }
    const next = multiple ? [...files, ...picked] : picked.slice(0, 1)
    if (next.length > MAX_PHOTOS) {
      setError(t('process_details_page.report_incident.too_many_photos'))
      return
    }
    setError(undefined)
    onChange(next)
  }

  return (
    <FieldRoot invalid={!!error} className="pb-4">
      <FieldLabel>{label}</FieldLabel>
      {/* Hidden because a file input cannot be styled; the button opens it. */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-label={label}
        accept={ACCEPTED_TYPES.join(',')}
        multiple={multiple}
        onChange={(e) => {
          pick(Array.from(e.target.files ?? []))
          // Cleared so picking the same file again after removing it still fires a change.
          e.target.value = ''
        }}
      />
      <Button
        type="button"
        variant="secondary"
        className="w-fit"
        onClick={() => inputRef.current?.click()}
      >
        {buttonLabel}
      </Button>
      {files.map((file, i) => (
        <div
          key={`${file.name}-${i}`}
          className="flex items-center gap-2 text-sm"
        >
          <span className="truncate">{file.name}</span>
          <IconButton
            size="sm"
            aria-label={t('process_details_page.report_incident.remove_file', {
              name: file.name
            })}
            onClick={() => onChange(files.filter((_, j) => j !== i))}
          >
            ×
          </IconButton>
        </div>
      ))}
      <FieldDescription>{description}</FieldDescription>
      {error && <FieldError match>{error}</FieldError>}
    </FieldRoot>
  )
}

export default IncidentFilePicker
